import React, { useState, useEffect, useCallback } from "react";
import "./AdminPage.css";
import "./EventFunnelPage.css";
import apiClient from "../api/client";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";
import usePlaylists from "../hooks/usePlaylists";
import { lastNDays, dateRangeList, formatOccurredAt } from "../utils/date";
import { scrollHorizontallyOnWheel } from "../utils/scroll";
import { useDragScroll } from "../hooks/useDragScroll";

const FUNNEL_LABELS = {
  app_open: "開啟 App",
  playback_started: "開始播放",
  playback_completed: "完成播放",
  rating_submitted: "完成評分",
  adverse_survey_submitted: "完成問卷",
};

const EVENT_LABELS = {
  app_open: "開啟 App",
  app_foreground: "App 回到前景",
  app_background: "App 背景化",
  playback_started: "開始／恢復播放",
  playback_paused: "暫停",
  playback_completed: "播放完成",
  rating_submitted: "完成評分",
  adverse_survey_submitted: "完成不良反應問卷",
  sync_result: "每日同步結果",
  midnight_rollover: "跨日切換",
};

// avg_volume 是 App 端存的 0.0~1.0 系統音量（見 PlaybackSessionController 的
// _avgVolumeEventPayload()），搭在 playback_paused/playback_completed 的 payload
// 裡一起送過來，不是每一種事件都會有這個欄位，沒有就不顯示、不補「?」佔位。
function formatAvgVolumeSuffix(payload) {
  if (payload.avg_volume == null) return "";
  return `・平均音量 ${Math.round(payload.avg_volume * 100)}%`;
}

function formatEventMeta(ev) {
  const p = ev.payload || {};
  switch (ev.event_name) {
    case "playback_started":
      return p.resume_from_pause
        ? `位置 ${p.start_position_sec ?? "?"} 秒・休息 ${p.rest_duration_sec ?? "?"} 秒後接續`
        : `位置 ${p.start_position_sec ?? "?"} 秒`;
    case "playback_paused":
      // 平均音量刻意不放在這裡：這個事件同一天會觸發很多次，每次都附上「目前
      // 累計到這一刻」的估算值，全部顯示在事件時間軸上只會看到同一個數字（或
      // 緩慢變化的數字）反覆出現很多遍，資訊太分散、看不出重點。真正有意義的
      // 單一平均值改成只在「依單曲分析」分頁的彙總數字裡看（見 SongTab）。
      return `位置 ${p.position_sec ?? "?"} 秒・本段播放 ${p.played_duration_this_segment_sec ?? "?"} 秒・原因：${p.pause_reason ?? "?"}`;
    case "playback_completed":
      return `完成度 ${p.completion_pct != null ? Math.round(p.completion_pct) : "?"}%・暫停 ${p.pause_count ?? 0} 次・總休息 ${p.total_rest_sec ?? 0} 秒${formatAvgVolumeSuffix(p)}`;
    case "rating_submitted":
      return `評分 ${p.rating_score ?? "?"} / 5`;
    case "app_background":
      return `播放中：${p.was_playing ? "是" : "否"}`;
    case "app_foreground":
      return `背景停留 ${p.background_duration_sec ?? "?"} 秒`;
    case "sync_result":
      return p.success ? "同步成功" : `同步失敗：${p.error_message ?? ""}`;
    case "adverse_survey_submitted":
      return ["q1", "q2", "q3", "q4"]
        .filter((q) => p[`${q}_yes`])
        .map((q) => `${q} 嚴重度 ${p[`${q}_severity`] ?? "?"}`)
        .join("、") || "無不適回報";
    default:
      return Object.keys(p).length ? JSON.stringify(p) : "";
  }
}

export default function EventFunnelPage({ isLoggedIn, onLogout }) {
  const tabsDragScroll = useDragScroll();
  const [activeTab, setActiveTab] = useState("overview");
  // 分組（沿用既有的 Playlist 群組概念）
  const { playlists: groups } = usePlaylists();
  const [selectedGroup, setSelectedGroup] = useState("");
  const [songs, setSongs] = useState([]);

  const handleConfirmLogout = () => {
    onLogout();
  };

  // 歌曲列表：依受試者分析／依單曲分析都需要（歌曲總長度用來換算進度條上的暫停位置），
  // 放在最上層抓一次，兩個分頁共用，避免重複打 API。
  useEffect(() => {
    const fetchSongs = async () => {
      try {
        const res = await apiClient.get("/api/songs");
        setSongs(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("取得歌曲列表失敗:", error);
      }
    };
    fetchSongs();
  }, []);

  return (
    <>
      <Header onLogout={handleConfirmLogout} />
      <div className="admin-container">
        <div className="admin-content">
          <div className="admin-header-row">
            <p className="admin-title">聆聽事件漏斗</p>
          </div>

          <div className="ef-toolbar">
            <label className="ef-toolbar-label">
              分組
              <select
                className="ef-select"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <option value="">全部</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <nav className="ef-tabs" onWheel={scrollHorizontallyOnWheel} {...tabsDragScroll}>
            <button
              className={`ef-tab-btn ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              總覽
            </button>
            <button
              className={`ef-tab-btn ${activeTab === "subject" ? "active" : ""}`}
              onClick={() => setActiveTab("subject")}
            >
              依受試者分析
            </button>
            <button
              className={`ef-tab-btn ${activeTab === "song" ? "active" : ""}`}
              onClick={() => setActiveTab("song")}
            >
              依單曲分析
            </button>
          </nav>

          {activeTab === "overview" && (
            <OverviewTab selectedGroup={selectedGroup} />
          )}
          {activeTab === "subject" && (
            <SubjectTab selectedGroup={selectedGroup} songs={songs} />
          )}
          {activeTab === "song" && (
            <SongTab selectedGroup={selectedGroup} songs={songs} />
          )}
        </div>
      </div>
    </>
  );
}

function OverviewTab({ selectedGroup }) {
  const [range, setRange] = useState(() => lastNDays(7));
  const [stages, setStages] = useState([]);
  const [adherence, setAdherence] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = { start: range.start, end: range.end };
    if (selectedGroup) params.group = selectedGroup;

    Promise.all([
      apiClient.get("/api/events/funnel", { params }),
      apiClient.get("/api/events/adherence", { params }),
    ])
      .then(([funnelRes, adherenceRes]) => {
        if (cancelled) return;
        setStages(funnelRes.data.stages || []);
        setAdherence(adherenceRes.data.adherence || {});
      })
      .catch((error) => {
        console.error("取得總覽資料失敗:", error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedGroup, range]);

  const maxCount = stages.length ? stages[0].count || 1 : 1;
  const days = dateRangeList(range.start, range.end);
  const users = Object.keys(adherence).sort();

  return (
    <div className="ef-panel">
      <div className="ef-toolbar ef-toolbar-tab">
        <label className="ef-toolbar-label">
          日期區間
          <input
            className="ef-date-input"
            type="date"
            value={range.start}
            max={range.end}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
          />
          <span className="ef-toolbar-range">～</span>
          <input
            className="ef-date-input"
            type="date"
            value={range.end}
            min={range.start}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
          />
        </label>
      </div>

      {loading && <p className="loading-text">載入中...</p>}

      {!loading && (
        <>
          <div className="ef-card">
            <h3 className="ef-card-title">事件漏斗</h3>
            <p className="ef-card-sub">開啟 App 到完成問卷的轉換人-日數，同一人同一天同一階段只算一次</p>
            <div className="ef-funnel">
              {stages.map((s) => {
                const pct = maxCount ? Math.round((s.count / maxCount) * 100) : 0;
                return (
                  <div className="ef-funnel-row" key={s.stage}>
                    <span className="ef-funnel-label">{FUNNEL_LABELS[s.stage] || s.stage}</span>
                    <div className="ef-funnel-track">
                      <div className="ef-funnel-fill" style={{ width: `${pct}%` }}>
                        <span>{s.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!stages.length && <p className="no-data">這段期間沒有事件資料</p>}
            </div>
          </div>

          <div className="ef-card">
            <h3 className="ef-card-title">依從性總覽</h3>
            <p className="ef-card-sub">每位受試者近 {days.length} 日是否完成當日曲目</p>
            <div className="ef-adherence-scroll">
              <table className="ef-adherence-table">
                <thead>
                  <tr>
                    <th>受試者</th>
                    {days.map((d) => (
                      <th key={d}>{d.slice(5)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((userId) => (
                    <tr key={userId}>
                      <td className="ef-adherence-user">{userId}</td>
                      {days.map((d) => {
                        const cell = adherence[userId][d];
                        let cls = "ef-cell-miss";
                        let title = "無資料";
                        if (cell) {
                          if (cell.completed && cell.anomaly) {
                            cls = "ef-cell-flag";
                            title = "已完成，但有異常";
                          } else if (cell.completed) {
                            cls = "ef-cell-done";
                            title = "已完成";
                          } else {
                            cls = "ef-cell-miss";
                            title = "未完成";
                          }
                        }
                        return (
                          <td key={d}>
                            <div className={`ef-cell ${cls}`} title={title} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {!users.length && (
                    <tr>
                      <td colSpan={days.length + 1} className="no-data">
                        這段期間沒有事件資料
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="ef-legend">
              <span><i className="ef-cell ef-cell-done" /> 已完成</span>
              <span><i className="ef-cell ef-cell-flag" /> 已完成但有異常</span>
              <span><i className="ef-cell ef-cell-miss" /> 未完成／無資料</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SubjectTab({ selectedGroup, songs }) {
  const listDragScroll = useDragScroll();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => lastNDays(1).end);
  const [events, setEvents] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    const fetchUsers = async () => {
      try {
        const url = selectedGroup
          ? `/api/groups/${selectedGroup}/users`
          : "/api/manager/users";
        const res = await apiClient.get(url);
        if (ignore) return;
        const list = Array.isArray(res.data)
          ? res.data.map((u) => (typeof u === "string" ? u : u.username)).filter(Boolean)
          : [];
        setUsers(list);
        if (list.length && !list.includes(selectedUser)) {
          setSelectedUser(list[0]);
        }
      } catch (error) {
        console.error("取得受試者列表失敗:", error);
      }
    };
    fetchUsers();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  // isStale() 由呼叫端（下面的 useEffect）提供，讓切換受試者/日期太快時，
  // 舊請求回來能自己判斷「我已經過期了」，不會用舊資料蓋掉新選擇的畫面。
  const loadEvents = useCallback(async (isStale) => {
    if (!selectedUser || !selectedDate) return;
    setLoading(true);
    setProgress(null);
    try {
      const res = await apiClient.get(
        `/api/events/subject/${selectedUser}/${selectedDate}`
      );
      if (isStale()) return;
      const fetchedEvents = res.data.events || [];
      setEvents(fetchedEvents);

      // 進度條需要知道當天播的是哪一首歌——直接採用 ListeningSession 的權威紀錄
      // （後端已依 user_id + date_id 查好），不能用事件序列裡第一筆帶 song_id 的
      // 事件去猜，事件序列可能混有其他歌曲的雜訊事件而導致誤判。
      const songId = res.data.song_id;
      if (songId) {
        try {
          const progressRes = await apiClient.get(
            "/api/events/song-progress",
            { params: { song_id: songId, date_id: selectedDate } }
          );
          if (isStale()) return;
          const subjectInfo = (progressRes.data.subjects || {})[selectedUser];
          setProgress(subjectInfo ? { ...subjectInfo, songId } : null);
        } catch (error) {
          console.error("取得受試者當日進度失敗:", error);
          if (!isStale()) setProgress(null);
        }
      }
    } catch (error) {
      console.error("取得受試者事件失敗:", error);
      if (!isStale()) setEvents([]);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [selectedUser, selectedDate]);

  useEffect(() => {
    let ignore = false;
    loadEvents(() => ignore);
    return () => {
      ignore = true;
    };
  }, [loadEvents]);

  const filteredUsers = users.filter((u) =>
    u.toLowerCase().includes(search.trim().toLowerCase())
  );

  const progressSong = progress ? songs.find((s) => s.song_id === progress.songId) : null;

  return (
    <div className="ef-panel ef-panel-split">
      <aside className="ef-sidebar">
        <input
          className="ef-search"
          type="text"
          placeholder="搜尋受試者代號…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ef-sidebar-list" onWheel={scrollHorizontallyOnWheel} {...listDragScroll}>
          {filteredUsers.map((u) => (
            <button
              key={u}
              className={`ef-sidebar-row ${u === selectedUser ? "active" : ""}`}
              onClick={() => setSelectedUser(u)}
            >
              {u}
            </button>
          ))}
          {!filteredUsers.length && <p className="no-data">找不到符合的受試者</p>}
        </div>
      </aside>

      <div className="ef-main">
        <div className="ef-card">
          <div className="ef-detail-head">
            <span className="ef-tab-heading">{selectedUser || "—"}</span>
            <input
              className="ef-date-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          {loading && <p className="loading-text">載入中...</p>}
          {!loading && progress && (
            <div className="ef-subject-progress">
              <div className="ef-subject-progress-head">
                <span className="ef-subject-progress-song">
                  {progressSong ? progressSong.title || progressSong.song_id : progress.songId}
                </span>
                <span className="ef-song-stat">
                  完成 {Math.round(Math.min(100, Math.max(0, progress.completion_pct || 0)))}%
                  ・暫停 {progress.pause_count || 0} 次
                  {progress.avg_volume != null && `・平均音量 ${Math.round(progress.avg_volume * 100)}%`}
                </span>
              </div>
              <ProgressBar
                completionPct={progress.completion_pct}
                pauses={progress.pauses}
                songDurationSec={progressSong?.duration || 0}
              />
            </div>
          )}
          {!loading && (
            <div className="ef-event-list">
              {events.map((ev) => (
                <div className="ef-event-item" key={ev.event_id}>
                  <span className="ef-event-time">
                    {formatOccurredAt(ev.occurred_at)}
                  </span>
                  <div className={`ef-event-rail ef-event-${ev.event_name}`}>
                    <div className="ef-event-title">
                      {EVENT_LABELS[ev.event_name] || ev.event_name}
                    </div>
                    <div className="ef-event-meta">{formatEventMeta(ev)}</div>
                  </div>
                </div>
              ))}
              {!events.length && (
                <p className="no-data">這一天沒有事件紀錄</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SongTab({ selectedGroup, songs }) {
  const listDragScroll = useDragScroll();
  const [search, setSearch] = useState("");
  const [selectedSongId, setSelectedSongId] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => lastNDays(1).end);
  const [subjects, setSubjects] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (songs.length && !selectedSongId) {
      setSelectedSongId(songs[0].song_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs]);

  const loadProgress = useCallback(async (isStale) => {
    if (!selectedSongId || !selectedDate) return;
    setLoading(true);
    try {
      const params = { song_id: selectedSongId, date_id: selectedDate };
      if (selectedGroup) params.group = selectedGroup;
      const res = await apiClient.get("/api/events/song-progress", { params });
      if (isStale()) return;
      setSubjects(res.data.subjects || {});
    } catch (error) {
      console.error("取得單曲進度失敗:", error);
      if (!isStale()) setSubjects({});
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [selectedSongId, selectedDate, selectedGroup]);

  useEffect(() => {
    let ignore = false;
    loadProgress(() => ignore);
    return () => {
      ignore = true;
    };
  }, [loadProgress]);

  const selectedSong = songs.find((s) => s.song_id === selectedSongId);
  const songDurationSec = selectedSong?.duration || 0;

  const filteredSongs = songs.filter((s) =>
    (s.title || s.song_id || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  // 整首歌（這個日期、所有受試者）的平均音量：不能對每位受試者的 avg_volume
  // 直接取簡單平均，那樣會讓「只取樣了 10 秒」跟「取樣了 300 秒」的受試者
  // 權重一樣重，扭曲結果——要用各自的 volume_sample_seconds 當權重加權平均。
  const subjectEntries = Object.entries(subjects);
  let volumeWeightedSum = 0;
  let volumeTotalSamples = 0;
  let subjectsWithVolume = 0;
  subjectEntries.forEach(([, info]) => {
    if (info.avg_volume == null || !info.volume_sample_seconds) return;
    volumeWeightedSum += info.avg_volume * info.volume_sample_seconds;
    volumeTotalSamples += info.volume_sample_seconds;
    subjectsWithVolume += 1;
  });
  const songAvgVolume = volumeTotalSamples > 0 ? volumeWeightedSum / volumeTotalSamples : null;

  return (
    <div className="ef-panel ef-panel-split">
      <aside className="ef-sidebar">
        <input
          className="ef-search"
          type="text"
          placeholder="搜尋歌曲名稱…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ef-sidebar-list" onWheel={scrollHorizontallyOnWheel} {...listDragScroll}>
          {filteredSongs.map((s) => (
            <button
              key={s.song_id}
              className={`ef-sidebar-row ${s.song_id === selectedSongId ? "active" : ""}`}
              onClick={() => setSelectedSongId(s.song_id)}
            >
              {s.title || s.song_id}
            </button>
          ))}
          {!filteredSongs.length && <p className="no-data">找不到符合的歌曲</p>}
        </div>
      </aside>

      <div className="ef-main">
        <div className="ef-card">
          <div className="ef-detail-head">
            <span className="ef-tab-heading">
              {selectedSong ? selectedSong.title || selectedSong.song_id : "—"}
            </span>
            <input
              className="ef-date-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          {!loading && songAvgVolume != null && (
            <p className="ef-card-sub">
              這首歌當天平均音量：{Math.round(songAvgVolume * 100)}%
              （依 {subjectsWithVolume} 位受試者的取樣秒數加權平均）
            </p>
          )}

          {loading && <p className="loading-text">載入中...</p>}
          {!loading && (
            <div className="ef-song-rows">
              {Object.entries(subjects).map(([userId, info]) => {
                const pct = info.completion_pct != null ? Math.min(100, Math.max(0, info.completion_pct)) : 0;
                return (
                  <div className="ef-song-row" key={userId}>
                    <span className="ef-song-user">{userId}</span>
                    <ProgressBar
                      completionPct={info.completion_pct}
                      pauses={info.pauses}
                      songDurationSec={songDurationSec}
                    />
                    <span className="ef-song-stat">
                      完成 {Math.round(pct)}%・暫停 {info.pause_count || 0} 次
                      {info.avg_volume != null && `・平均音量 ${Math.round(info.avg_volume * 100)}%`}
                    </span>
                  </div>
                );
              })}
              {!Object.keys(subjects).length && (
                <p className="no-data">這首歌這一天沒有事件資料</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
